import React, { useMemo } from "react";
import { useDispatch } from "react-redux";

import {
    setResultsOpen as setResultsOpenAction,
    setReportLevelWithColumns,
    setReportTypeWithColumns,
    setSelectedIds as setSelectedIdsAction,
    setSearchString as setSearchStringAction,
    setPageNumber as setPageNumberAction,
    setLimitNumber as setLimitNumberAction,
    setSortData as setSortDataAction,
    setFilterData as setFilterDataAction,
    setError as setErrorAction,
    fetchReportColumns,
    fetchReportResults,
    exportReport,
    setConflictLevelWithColumns,
    calcReport,
    saveReportColumns,
} from "../../../reducers/reports-modeling/userLevelSlice";

import useReportReduxTable from "../../common/hooks/useReportReduxTable";

import ReportResults from "../../sod-reports/report-results";
import ConflictLevelsPicker from "../conflict-levels-picker";
import { REPORT_MODELING_CONFLICT_LEVELS_USER } from "../../../utils/reports-modeling-utils.ts";
import useReduxValue from "../../common/hooks/useReduxValue";
import { reportsModelingService } from "../../../services/reports-modeling-service.ts";

const getColumns = (reportLevel, reportType, defaultColumns) =>
    reportsModelingService.getUserLevelModelingColumns(reportLevel, reportType, defaultColumns);

const UserLevelModelingResults = () => {
    const dispatch = useDispatch();

    const {
        rows,
        columns,
        total,
        busy,
        selectedIds,
        searchString,
        setSearchString,
        pageNumber,
        setPageNumber,
        limitNumber,
        setLimitNumber,
        filterData,
        setFilterData,
        sortData,
        setSortData,
        reportLevel,
        setReportLevel,
        reportType,
        setReportType,
        open,
        setOpen,
    } = useReportReduxTable(
        (state) => state.reportsModeling.userLevel.report.rows,
        fetchReportResults,
        (state) => state.reportsModeling.userLevel.report.columns,
        fetchReportColumns,
        (state) => state.reportsModeling.userLevel.report.selectedIds,
        setSelectedIdsAction,
        (state) => state.reportsModeling.userLevel.report.searchString,
        setSearchStringAction,
        (state) => state.reportsModeling.userLevel.report.pageNumber,
        setPageNumberAction,
        (state) => state.reportsModeling.userLevel.report.limitNumber,
        setLimitNumberAction,
        (state) => state.reportsModeling.userLevel.report.sortData,
        setSortDataAction,
        (state) => state.reportsModeling.userLevel.report.filterData,
        setFilterDataAction,
        (state) => state.reportsModeling.userLevel.report.error,
        setErrorAction,
        (state) => state.reportsModeling.userLevel.report.total,
        (state) => state.reportsModeling.userLevel.report.busy,
        (state) => state.reportsModeling.userLevel.report.reportLevel,
        setReportLevelWithColumns,
        (state) => state.reportsModeling.userLevel.report.reportType,
        setReportTypeWithColumns,
        (state) => state.reportsModeling.userLevel.report.resultsOpen,
        setResultsOpenAction
    );

    const [conflictLevel] = useReduxValue((state) => state.reportsModeling.userLevel.report.conflictLevel, setConflictLevelWithColumns);

    const handleExportClick = () => {
        dispatch(exportReport());
    };

    const changeConflictLevel = async (conflictLevel) => {
        await dispatch(calcReport({ conflictLevel: conflictLevel, calcWithReset: false })).unwrap();
        await dispatch(fetchReportColumns());
        // await setConflictLevel(conflictLevel)
    };

    const conflictLevels = useMemo(() => {
        return Object.values(REPORT_MODELING_CONFLICT_LEVELS_USER);
    }, []);

    const handleSaveColumns = (columns, reportLevel, reportType) => {
        dispatch(
            saveReportColumns({
                columns,
                reportLevel,
                reportType,
            })
        );
    };

    return (
        <ReportResults
            open={open}
            setOpen={setOpen}
            reportLevel={reportLevel}
            setReportLevel={setReportLevel}
            reportType={reportType}
            setReportType={setReportType}
            rows={rows}
            columns={columns}
            total={total}
            busy={busy}
            selectedIds={selectedIds}
            searchString={searchString}
            setSearchString={setSearchString}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            limitNumber={limitNumber}
            setLimitNumber={setLimitNumber}
            filterData={filterData}
            setFilterData={setFilterData}
            sortData={sortData}
            setSortData={setSortData}
            onExportClick={handleExportClick}
            filterId="user-report"
            collapseChildren={<ConflictLevelsPicker value={conflictLevel} conflictLevels={conflictLevels} onChange={changeConflictLevel} />}
            conflictLevel={conflictLevel}
            getColumns={getColumns}
            saveReportColumns={handleSaveColumns}
        />
    );
};

export default UserLevelModelingResults;
